import { useEffect, useState, useMemo } from 'react';
import Head from 'next/head';
import { generateNextSeo } from 'next-seo/pages';
import { ProductJsonLd, FAQJsonLd } from 'next-seo';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import get from 'lodash/get';

/* Integrations */
import { queryClient } from '@integrations/reactquery';

/* Preval Data */
import plans from '@integrations/preval/plans.preval';

// Components
import ServiceInfo from '@scenes/ServiceSkuPage/Parts/ServiceInfo';
import LazyComponent from '@components/LazyComponent';
import { shouldShowZipCodeTest } from '@components/Sku/ZipCodeEntry';
import Maybe from '@components/Maybe';
import SpaceY from '@components/UI/Space/Space.y-axis';

/* GQL & Queries */
// import useSubscriptionsQueryAll from '@store/queries/subscription/useSubscriptionsQuery.all';
import useSubscriptionsQueryEstimate from '@store/queries/subscription/useSubscriptionsQuery.estimate';
import useCartMutationCanAddService from '@store/queries/cart/useCartMutation.canaddservice';
import useCartCache from '@store/queries/cart/useCart.cache';
import useUserCache from '@store/queries/user/useUser.cache';
import useCartMutationRemoveplan from '@store/queries/cart/useCartMutation.removeplan';
import useCartMutationSelectplan from '@store/queries/cart/useCartMutation.selectplan';
import { skuQueryKeys } from '@store/queries/sku/queries.keys';
import useCartMutationStartBooking, { handleSingleServiceBookingSuccess } from '@store/queries/cart/useCartMutation.startbooking';

/* Store */
import useServiceSkuStore, { ServiceSkuState } from '@scenes/ServiceSkuPage/store/store.servicesku';
import { useResponsiveIsMobile } from '@store/client/store.responsive';

/* Integrations */
import { SegmentTrackingAction } from '@integrations/segment/sku';

/* Utils */
import { formatPlansForReducer, getUpsellPlanIds } from '@utils/plans';
import { displayTimestamp } from '@utils/date';
import { mergeSkuData } from '@scenes/ServiceSkuPage/servicesku.utils';
import { skuPrices } from '@utils/sku';
import { makeSkuJsonLd, makeRichFaqJsonLd } from '@utils/jsonLd/sku';
import SocialMetaTags from '@utils/seo/SocialMetaTags';
import { isMobileOrTablet } from '@utils/ui';
import { getZipDataCookie } from '@utils/cookies/zipCodeTestCookie';
import { smoothScrollTo } from '@utils/helpers';
import { useCartFlow } from '@utils/cart/useCartFlow';

/* Hooks */
import useScrollTrigger from '@hooks/useScrollTrigger';
import useSplit from '@integrations/split/useSplit';
import useBESplitMutationSkuPrice from '@hooks/useBESplitSkuPrice';
import { useIsPasswordlessCheckoutEnabled } from '@scenes/LoginPage/Modal/LoginPasscode/LoginPasscode.hooks';

/* Constants */
import { SERVICE_HEADER_TRIGGER_ID } from '@scenes/ServiceSkuPage/Parts/ServiceHeader/constants';
import { SPLITIONAME_CUSTOMER_ZIP } from '@integrations/split/split.constants';
import { BREAKDOWN_PRICE_TYPES, ITEM_REMOVAL_TOAST_MESSAGES } from '@constants/cart';

/* styles */
import styles from '@scenes/ServiceSkuPage/servicesku.module.scss';

/* Types */
import { ServicesPageSkuProps, Sku } from 'types/sku.types';
import { Review } from 'types/reviews.types';
import { TPlansInfoFeatured } from 'types/plans.types';
import { cartPath } from 'src/constants/paths';

/* Components Dynamic */
const PlanModalDynamic = dynamic(() => import('@components/Modals/PlanModal'), { ssr: false });
const ServicePlanBannerDynamic = dynamic(() => import('@components/UI/Banner/PageBannerWithCta/index'), { ssr: false });
const ServiceHeaderDynamic = dynamic(() => import('@scenes/ServiceSkuPage/Parts/ServiceHeader/ServiceHeader'), { ssr: false });
const ServiceBookNowDynamic = dynamic(() => import('@scenes/ServiceSkuPage/Parts/ServiceBookNow/index'), { ssr: false });
const SkuItemDetailsDynamic = dynamic(() => import('@scenes/ServiceSkuPage/Parts/ServiceDetails'), { ssr: false });
const ExpertNationWideServiceDynamic = dynamic(() => import('@components/Sku/ExpertNationWideService'), { ssr: false });
const InTheNewsDynamic = dynamic(() => import('@components/Sku/InTheNews'), { ssr: false });
const SkuItemGuaranteeDynamic = dynamic(() => import('@scenes/ServiceSkuPage/Parts/ServiceGuarantee'), { ssr: false });
const SkuItemBannerDynamic = dynamic(() => import('@components/Sku/SkuItemBanner/SkuItemBanner'), { ssr: false });
const SkuItemFaqDynamic = dynamic(() => import('@components/Sku/SkuItemFaq'), { ssr: false });
const CustomerReviewsDynamic = dynamic(() => import('@components/UI/Reviews'), { ssr: false });
const MaxServicesModalDynamic = dynamic(() => import('@components/Modals/SingleServiceExceedMaxServices'), { ssr: false });

const ServicesSkuPage = (props: ServicesPageSkuProps) => {
  let { sku: service = {} as Sku } = props;
  const { reviews: skuReviews = [] as Review[], partner } = props;

  /* Since this is a SSG page, and not SSR - no rehydration. Lets just set the cache manually here */
  queryClient.setQueryData(skuQueryKeys.sku(service.id), { ...service });

  /* local state/store */
  const { isZipSuccessViewActive, setZipSuccessViewActive } = useServiceSkuStore((state: ServiceSkuState) => state.zipCodeGate);
  const [showPlanModal, setShowPlanModal] = useState<boolean>(false);
  const plansInfo = useMemo<TPlansInfoFeatured | null>(() => {
    if (!plans.length) return null;

    return formatPlansForReducer({
      planProducts: plans,
      featured: false,
      isMobileOrTablet: isMobileOrTablet(),
    });
  }, []);

  /* Scroll secondary header to show */
  const [visible, setVisible] = useState(false);
  /* end local state */

  /*  Hooks */
  const cart = useCartCache();
  const user = useUserCache();
  const router = useRouter();

  useScrollTrigger({
    trigger: `#${SERVICE_HEADER_TRIGGER_ID}`,
    onReach: v => setVisible(v),
  });

  /* Split Hooks */
  const { treatment: geoZipEmailSplitTreatment, isSplitLoaded: geoZipEmailSplitLoaded } = useSplit(SPLITIONAME_CUSTOMER_ZIP);
  const isPasswordlessCheckoutEnabled = useIsPasswordlessCheckoutEnabled();

  /* component constants */
  const reviews = skuReviews?.map(r => ({ ...r, date: displayTimestamp(r.timestamp) }));
  const subscriptionId = get(user, 'account.subscription.planId', null);
  const cartPlanId = get(cart, 'plan.id', null);
  const { monthlyID: MONTHLY_PLAN_ID } = getUpsellPlanIds({});

  /* Single Sku Concerns Constants */
  const { getSkuPageConfig, isSingleServiceFlow } = useCartFlow();
  const existingSkuInCart = cart?.items?.[0];
  const cartFlowConfig = getSkuPageConfig({
    newSkuId: service?.id,
    existingSkuId: existingSkuInCart?.skuId,
  });

  const [showServicesWarningModal, setShowServicesWarningModal] = useState(false);

  /* QUERIES AND MUTATIONS ------------------------ */
  /* get subscriptions data, query */
  // const {data: {plans = []} = {}} = useSubscriptionsQueryAll({});
  const { data: { prices: subscriptionPrices = {} } = {} } = useSubscriptionsQueryEstimate({
    id: subscriptionId || cartPlanId || MONTHLY_PLAN_ID,
    skuId: service.id,
  });
  const REVIEW_SCROLL_ID = 'reviews';

  /* mutations */
  /* mutate: call later: book now */
  const { mutate: fetchCanAddService } = useCartMutationCanAddService({ skuId: service.id });
  /* end get subscriptions data */
  const selectPlanMutation = useCartMutationSelectplan();
  const removePlanMutation = useCartMutationRemoveplan();
  const { data: cpsaSkuPrice, isPending } = useBESplitMutationSkuPrice(service.id, {});
  const startBookingMutation = useCartMutationStartBooking();

  /* end mutations */

  /* END QUERIES AND MUTATIONS ------------------------ */

  /* create merged attributes into sku */
  // merge new cpsaSkuPrice into service
  service = { ...service, ...(cpsaSkuPrice?.sku?.id && cpsaSkuPrice?.sku) };
  const prices = skuPrices(service, cart!, subscriptionPrices);
  const isHourly = service?.priceType === BREAKDOWN_PRICE_TYPES.hourly;

  const showUpsell = (!user?.id || !user?.account?.hasSubscription) && !!prices.priceDifference && !isHourly;

  /* Determine Zip/Email Display */
  const { isBookingAllowed } = getZipDataCookie() || {};
  const showZipCodeTest =
    shouldShowZipCodeTest(isBookingAllowed, geoZipEmailSplitLoaded && geoZipEmailSplitTreatment, !!user?.id, !!partner?.id) &&
    geoZipEmailSplitTreatment;

  /* Merge sku data: extra sku.extra */
  const extendedSkuAttributes = mergeSkuData({
    cart,
    user,
    showUpsell,
    showZipCodeTest,
    prices,
    sku: service,
  });

  /* end create merged attributes into sku */
  /* Merge sku */
  const sku = useMemo(
    () => ({
      ...service,
      ...extendedSkuAttributes,
      plansInfo,
      subscriptionPrices,
    }),
    [extendedSkuAttributes, plansInfo, service, subscriptionPrices],
  );

  /* Client Store */
  const isMobile = useResponsiveIsMobile();

  /* -------- METHODS --------------------- */
  /* This is just nonsense. Now our buttons can be different actions in the various sub-headers. */
  const onHandleBookingClick = () => {
    if (showZipCodeTest) {
      window.scrollTo({
        top: 0,
      });

      return;
    }

    fetchCanAddService();
  };

  /* Now we have single service concerns, this navigates reg & single service */
  const initiateBookingProcess = () => {
    if (cartFlowConfig.showWarning) {
      setShowServicesWarningModal(true);
      return;
    }
    onHandleBookingClick();
  };

  /* Scroll to reviews */
  const handleRating = () => {
    smoothScrollTo({ elem: REVIEW_SCROLL_ID });
  };

  /* Toggle Modal & get Plan */
  const handleUpsellMore = async () => {
    setShowPlanModal(true);
  };

  /* Add/remove plan from upsell modal */
  const handleUpsellToggle = (shouldAdd: boolean, planId = MONTHLY_PLAN_ID) => {
    if (shouldAdd) {
      selectPlanMutation.mutate({ planId });
      setShowPlanModal(false);
    } else {
      removePlanMutation.mutate({ toastMessage: ITEM_REMOVAL_TOAST_MESSAGES.DEFAULT });
    }
  };
  /* -------- END METHODS --------------------- */

  /* -------- USE EFFECTS --------------------- */
  /*
   * Gladly weirdness - chat in as a param and only on this page, breaks Gladly
   * I reported, but just in case they don't fix in time. Here is a workaround.
   * */

  useEffect(() => {
    if (router.query.chat) {
      /* Create new query object, remove 'chat' and add 'openChat' */
      const { chat, ...restQuery } = router.query;
      const newQuery = {
        ...restQuery,
        openCustomerService: 'true',
      };

      /* Replace URL with new query parameter */
      router.replace(
        {
          pathname: router.pathname,
          query: newQuery,
        },
        undefined,
        { shallow: true }, // Prevents page refresh
      );
    }
  }, [router.query, MONTHLY_PLAN_ID, removePlanMutation, selectPlanMutation, router]);

  /* fire segment on load & seed current sku view in cache */
  useEffect(() => {
    SegmentTrackingAction.skuPage(sku);
  }, [sku]);

  useEffect(() => {
    if (!isZipSuccessViewActive) setZipSuccessViewActive(true);

    return () => {
      if (isZipSuccessViewActive) setZipSuccessViewActive(false);
    };
  }, [isZipSuccessViewActive, setZipSuccessViewActive]);

  // setZipDataCookie({...zipDataCookie, zipCode: zip, isBookingAllowed: true});
  return (
    <>
      <Head>{generateNextSeo({ title: sku.meta.title, description: sku.meta.description })}</Head>
      <SocialMetaTags sku={sku} />
      <ProductJsonLd {...makeSkuJsonLd(sku)} />
      <FAQJsonLd {...makeRichFaqJsonLd(sku)} />
      {visible && <ServiceHeaderDynamic visible={visible} sku={sku} onBook={initiateBookingProcess} onInfo={handleUpsellMore} />}
      {visible && <ServiceBookNowDynamic sku={sku} onBook={initiateBookingProcess} />}
      <ServiceInfo
        sku={sku}
        onBook={initiateBookingProcess}
        onRating={handleRating}
        onUpsellToggle={handleUpsellToggle}
        onUpsellMore={handleUpsellMore}
        className={styles.info}
        isPending={isPending}
      />

      <SpaceY classes="paddingTop-large1" />
      <SkuItemBannerDynamic />
      {/* Only deal with Faq if there are faq's */}
      <Maybe condition={sku.faq?.length > 0}>
        <SpaceY classes="paddingTop-large2" />
        <SkuItemFaqDynamic items={sku.faq || []} />
      </Maybe>

      <LazyComponent minHeight="400px">
        <SkuItemGuaranteeDynamic />
      </LazyComponent>

      {/* Only deal with Reviews if there are reviews */}
      <div id={REVIEW_SCROLL_ID} />
      <Maybe condition={reviews.length > 2}>
        <SpaceY classes="paddingTop-large2" />
        <LazyComponent minHeight={isMobile ? '250px' : '330px'}>
          <CustomerReviewsDynamic
            className="paddingX-medium"
            reviews={reviews}
            headerText="What our customers say"
            headerStyles="text-align-center"
            removeBackground={true}
            addShadow={false}
          />
        </LazyComponent>
      </Maybe>

      {/* Only deal with plan upset if we need to show it */}
      <Maybe condition={showUpsell}>
        <SpaceY classes="paddingTop-large" />
        <LazyComponent minHeight={isMobile ? '200px' : '48px'}>
          <ServicePlanBannerDynamic text={`HelloTech Home members save up to ${prices.priceDifference} on this service.`} onMore={handleUpsellMore} />
        </LazyComponent>
      </Maybe>

      <LazyComponent minHeight="500px">
        <>
          <SkuItemDetailsDynamic sku={sku} />
          <ExpertNationWideServiceDynamic />
          <InTheNewsDynamic />
        </>
      </LazyComponent>

      {/* modal */}
      <Maybe condition={showPlanModal}>
        <PlanModalDynamic
          /* @ts-ignore $FixMe */
          sku={sku}
          visible={showPlanModal}
          closePlanModal={() => setShowPlanModal(false)}
          addPlan={() => handleUpsellToggle(true)}
          plansInfo={plansInfo}
        />
      </Maybe>
      <Maybe condition={showServicesWarningModal}>
        <MaxServicesModalDynamic
          isVisible={showServicesWarningModal}
          hide={() => setShowServicesWarningModal(false)}
          onKeepServiceClick={() =>
            startBookingMutation.mutate(undefined, {
              onSuccess: () => {
                if (isSingleServiceFlow()) {
                  handleSingleServiceBookingSuccess({ router, isPasswordless: isPasswordlessCheckoutEnabled });
                } else {
                  router.push(cartPath());
                }
              },
            })
          }
          onSelectNewService={onHandleBookingClick}
          newSkuToAdd={sku}
          existingSkuInCart={existingSkuInCart}
        />
      </Maybe>
    </>
  );
};

export default ServicesSkuPage;
